import * as React from "react";
import { Field, Input, Select } from "../components/Form";
import { Button, IconButton } from "../components/Button";
import { Badge } from "../components/Badge";
import { Icon } from "../components/Icon";
import {
    INPUT_ANCHORS,
    INPUT_ANCHOR_LABELS,
    INPUT_SELECTOR_KINDS,
    INPUT_SELECTOR_LABELS,
    RuleDraft,
    SOURCE_MODULE_KINDS,
    SOURCE_MODULE_LABELS,
    ruleNeeds,
} from "./vocabulary";

/**
 * One input rule of a procedure version.
 *
 * A rule says: take marks of this mark type, found by this selector, relative to this anchor, and
 * feed them into the calculation with this weight. The selector is the interesting part - it matches
 * on *types* (assessment type, group type) rather than on specific entities, which is what lets one
 * procedure apply to every module that follows the same shape.
 *
 * Which fields a rule needs depends on its selector kind, so they are shown conditionally rather
 * than as a wall of mostly-irrelevant inputs.
 */

export interface Option {
    value: string;
    label: string;
}

export interface RuleEditorProps {
    rule: RuleDraft;
    index: number;
    /** Weight only means anything for a weighted average. */
    showWeight: boolean;
    markTypes: Option[];
    assessmentTypes: Option[];
    groupTypes: Option[];
    modules: Option[];
    onChange: (patch: Partial<RuleDraft>) => void;
    onRemove: () => void;
    disabled?: boolean;
}

export const RuleEditor: React.FC<RuleEditorProps> = ({
    rule,
    index,
    showWeight,
    markTypes,
    assessmentTypes,
    groupTypes,
    modules,
    onChange,
    onRemove,
    disabled,
}) => {
    const needs = ruleNeeds(rule.inputKind);

    return (
        <div className="rounded-lg border border-border bg-surface p-4">
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Badge tone="neutral">Rule {index + 1}</Badge>
                    <span className="text-sm text-text-weak">{INPUT_SELECTOR_LABELS[rule.inputKind] ?? rule.inputKind}</span>
                </div>
                <IconButton icon={["fad", "trash"]} variant="outline-danger" label={`Remove rule ${index + 1}`} onClick={onRemove} disabled={disabled} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Which mark feeds in" required className="mb-0" hint="The mark type the engine reads from each matched input.">
                    <Select
                        value={rule.componentMarkTypeID}
                        placeholder="Select a mark type"
                        disabled={disabled}
                        onChange={(e) => onChange({ componentMarkTypeID: e.target.value })}
                    >
                        {markTypes.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </Select>
                </Field>

                {showWeight && (
                    <Field label="Weight" className="mb-0" hint="Relative to the other rules. Weights do not have to add up to anything.">
                        <Input
                            type="number"
                            value={rule.weight === undefined ? "" : String(rule.weight)}
                            placeholder="1"
                            disabled={disabled}
                            onChange={(e) => onChange({ weight: e.target.value === "" ? undefined : Number(e.target.value) })}
                        />
                    </Field>
                )}

                <Field label="Look in" className="mb-0">
                    <Select
                        value={rule.sourceModuleKind}
                        disabled={disabled}
                        onChange={(e) => onChange({ sourceModuleKind: e.target.value as RuleDraft["sourceModuleKind"] })}
                    >
                        {SOURCE_MODULE_KINDS.map((k) => (
                            <option key={k} value={k}>
                                {SOURCE_MODULE_LABELS[k]}
                            </option>
                        ))}
                    </Select>
                </Field>

                {rule.sourceModuleKind === "SpecificModule" && (
                    <Field label="Module" className="mb-0" hint="Reading across subjects - e.g. a combined mark that pulls from another module.">
                        <Select
                            value={rule.sourceModuleID ?? ""}
                            placeholder="Select a module"
                            disabled={disabled}
                            onChange={(e) => onChange({ sourceModuleID: e.target.value || undefined })}
                        >
                            {modules.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </Field>
                )}

                <Field label="Relative to" className="mb-0">
                    <Select
                        value={rule.inputAnchor}
                        disabled={disabled}
                        onChange={(e) => onChange({ inputAnchor: e.target.value as RuleDraft["inputAnchor"] })}
                    >
                        {INPUT_ANCHORS.map((a) => (
                            <option key={a} value={a}>
                                {INPUT_ANCHOR_LABELS[a]}
                            </option>
                        ))}
                    </Select>
                </Field>

                <Field label="Find" className="mb-0">
                    <Select
                        value={rule.inputKind}
                        disabled={disabled}
                        onChange={(e) =>
                            // Changing the kind invalidates the fields the previous kind needed, so
                            // they are cleared rather than left to be sent as stale values.
                            onChange({
                                inputKind: e.target.value as RuleDraft["inputKind"],
                                groupPath: [],
                                assessmentTypeID: undefined,
                                assessmentGroupTypeID: undefined,
                                concreteEntityType: undefined,
                                concreteEntityID: undefined,
                            })
                        }
                    >
                        {INPUT_SELECTOR_KINDS.map((k) => (
                            <option key={k} value={k}>
                                {INPUT_SELECTOR_LABELS[k]}
                            </option>
                        ))}
                    </Select>
                </Field>

                {needs.assessmentType && (
                    <Field label="Assessment type" className="mb-0">
                        <Select
                            value={rule.assessmentTypeID ?? ""}
                            placeholder="Any"
                            disabled={disabled}
                            onChange={(e) => onChange({ assessmentTypeID: e.target.value || undefined })}
                        >
                            {assessmentTypes.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </Field>
                )}

                {needs.groupType && (
                    <Field label="Group type" className="mb-0">
                        <Select
                            value={rule.assessmentGroupTypeID ?? ""}
                            placeholder="Any"
                            disabled={disabled}
                            onChange={(e) => onChange({ assessmentGroupTypeID: e.target.value || undefined })}
                        >
                            {groupTypes.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </Field>
                )}

                {needs.concrete && (
                    <>
                        <Field label="Entity type" className="mb-0">
                            <Select
                                value={rule.concreteEntityType ?? ""}
                                placeholder="Select"
                                disabled={disabled}
                                onChange={(e) => onChange({ concreteEntityType: e.target.value || undefined })}
                            >
                                <option value="AssessmentGroup">Assessment group</option>
                                <option value="Assessment">Assessment</option>
                            </Select>
                        </Field>
                        <Field
                            label="Entity id"
                            className="mb-0"
                            hint="Ties this rule to one specific entity, so it stops being portable across modules."
                        >
                            <Input
                                value={rule.concreteEntityID ?? ""}
                                disabled={disabled}
                                onChange={(e) => onChange({ concreteEntityID: e.target.value || undefined })}
                            />
                        </Field>
                    </>
                )}
            </div>

            {needs.groupPath && (
                <GroupPathEditor
                    label="Path of group types"
                    path={rule.groupPath}
                    groupTypes={groupTypes}
                    disabled={disabled}
                    onChange={(groupPath) => onChange({ groupPath })}
                />
            )}
        </div>
    );
};

/**
 * A path is an ordered list of group *types* walked down from the anchor - "Term" then "Division",
 * for example. It is a list of types rather than of groups precisely so the same procedure resolves
 * in every module that uses the same tree shape.
 */
export const GroupPathEditor: React.FC<{
    label: string;
    path: string[];
    groupTypes: Option[];
    disabled?: boolean;
    onChange: (path: string[]) => void;
}> = ({ label, path, groupTypes, disabled, onChange }) => (
    <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
            <div>
                <span className="text-sm font-medium text-text">{label}</span>
                <p className="text-xs text-text-weak">Walked from the anchor downwards. Types, not particular groups.</p>
            </div>
            <Button
                size="sm"
                variant="outline-primary"
                icon={["fad", "plus"]}
                disabled={disabled}
                onClick={() => onChange([...path, ""])}
            >
                Add level
            </Button>
        </div>

        {path.length === 0 && <p className="text-sm text-text-weak">No levels - the selector applies directly at the anchor.</p>}

        <div className="space-y-2">
            {path.map((segment, i) => (
                <div key={i} className="flex items-center gap-2">
                    <Icon icon={["fad", "arrow-turn-down-right"]} className="text-text-weak" style={{ marginLeft: `${i * 1}rem` }} />
                    <Select
                        value={segment}
                        placeholder="Select a group type"
                        disabled={disabled}
                        className="flex-1"
                        onChange={(e) => onChange(path.map((s, j) => (j === i ? e.target.value : s)))}
                    >
                        {groupTypes.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </Select>
                    <IconButton
                        icon={["fad", "xmark"]}
                        label={`Remove level ${i + 1}`}
                        disabled={disabled}
                        onClick={() => onChange(path.filter((_, j) => j !== i))}
                    />
                </div>
            ))}
        </div>
    </div>
);

export default RuleEditor;
